import React, { useState, useEffect, useCallback } from 'react';
import {
  KeyRound, Loader2, AlertCircle, CheckCircle2, Copy, Check, XCircle, RotateCcw,
} from 'lucide-react';
import apiClient from '../utils/apiClient';
import gateSystemsService from '../services/gateSystemsService';

/**
 * GateAccessSection — rental detail page card.
 *
 * State machine:
 *   No gate_system configured     -> render nothing
 *   Configured, no record yet     -> [Provision gate access] button
 *   Status: pending               -> Operator paste form
 *   Status: provisioned           -> Code display + Revoke / Resend
 *   Status: revoked               -> [Provision new access]
 */

const GateAccessSection = ({ facilityId, rentalId, customer, unit }) => {
  const [facility, setFacility] = useState(null);
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pasteCode, setPasteCode] = useState('');
  const [pasteNotes, setPasteNotes] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    if (!facilityId || !rentalId) return;
    setLoading(true);
    try {
      const [facRes, accRes] = await Promise.all([
        apiClient.get(`/api/facilities/${facilityId}`),
        gateSystemsService.listAccess(facilityId, { rental_id: rentalId }),
      ]);
      setFacility(facRes.data);
      // Latest record wins.
      const records = accRes.data || [];
      setRecord(records.length ? records[0] : null);
    } catch (err) {
      console.error('Failed to load gate access:', err);
    } finally {
      setLoading(false);
    }
  }, [facilityId, rentalId]);

  useEffect(() => { load(); }, [load]);

  const provision = async () => {
    setBusy(true);
    try {
      const res = await gateSystemsService.createAccess(facilityId, rentalId);
      setRecord(res.data);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to provision access');
    } finally {
      setBusy(false);
    }
  };

  const submitPaste = async () => {
    if (!pasteCode) return;
    setBusy(true);
    try {
      const res = await gateSystemsService.operatorPaste(facilityId, record._id, {
        access_code: pasteCode,
        notes: pasteNotes || null,
      });
      setRecord(res.data);
      setPasteCode('');
      setPasteNotes('');
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to submit code');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm('Revoke this tenant\'s gate access? This cannot be undone — provision a new access record if you change your mind.')) return;
    const reason = window.prompt('Reason for revoking (optional)') || '';
    setBusy(true);
    try {
      const res = await gateSystemsService.revoke(facilityId, record._id, reason);
      setRecord(res.data);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to revoke access');
    } finally {
      setBusy(false);
    }
  };

  const copyCode = () => {
    if (!record?.access_code) return;
    navigator.clipboard?.writeText(record.access_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200/80 bg-white p-5 mb-4">
        <div className="flex items-center gap-2 text-slate-500 text-[13px]">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading gate access...
        </div>
      </div>
    );
  }

  // Facility has no gate integration — hide section entirely.
  if (!facility?.gate_system || !facility.gate_system.is_active) {
    return null;
  }

  const status = record?.status;

  return (
    <div id="gate-access" className="rounded-xl border border-slate-200/80 bg-white p-5 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <KeyRound className="h-4 w-4 text-blue-600" />
        <h3 className="text-[14px] font-semibold text-slate-900">Gate Access</h3>
      </div>

      {!record && (
        <div>
          <p className="text-[13px] text-slate-600 mb-3">
            No access has been provisioned for this rental yet.
          </p>
          <button
            type="button"
            onClick={provision}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-[13px] hover:bg-blue-700 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />}
            Provision gate access
          </button>
        </div>
      )}

      {status === 'pending' && (
        <div>
          <div className="flex items-start gap-2 mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200/60 text-amber-800 text-[13px]">
            <AlertCircle className="h-4 w-4 mt-0.5" />
            <div>
              <p className="font-medium">Operator action needed</p>
              <p className="text-[12px] mt-0.5">
                Create the access code in your gate system using the tenant
                details below, then paste it here to deliver the welcome email.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-[12px] text-slate-600 mb-3">
            <div><span className="text-slate-400">Name:</span> {customer?.full_name || '-'}</div>
            <div><span className="text-slate-400">Phone:</span> {customer?.phone || '-'}</div>
            <div><span className="text-slate-400">Email:</span> {customer?.email || '-'}</div>
            <div><span className="text-slate-400">Unit:</span> {unit?.unit_number || '-'}</div>
          </div>
          <div className="space-y-2">
            <input
              type="text"
              value={pasteCode}
              onChange={(e) => setPasteCode(e.target.value)}
              placeholder="Paste access code from your gate system"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] font-mono"
            />
            <input
              type="text"
              value={pasteNotes}
              onChange={(e) => setPasteNotes(e.target.value)}
              placeholder="Notes (optional)"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px]"
            />
            <button
              type="button"
              onClick={submitPaste}
              disabled={busy || !pasteCode}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-[13px] hover:bg-blue-700 disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              Submit code
            </button>
          </div>
        </div>
      )}

      {status === 'provisioned' && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[11px] font-medium">Active</span>
          </div>
          <div className="flex items-center gap-2 mb-3">
            <span className="font-mono text-xl tracking-wider px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg">
              {record.access_code}
            </span>
            <button
              type="button"
              onClick={copyCode}
              className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50"
              aria-label="Copy code"
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-emerald-600" />
              ) : (
                <Copy className="h-3.5 w-3.5 text-slate-500" />
              )}
            </button>
          </div>
          <p className="text-[12px] text-slate-500 mb-3">
            Provisioned {record.provisioned_at?.slice(0, 10) || '-'} by {record.provisioned_by || 'system'}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={revoke}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 text-red-700 text-[13px] hover:bg-red-50 disabled:opacity-50"
            >
              <XCircle className="h-3.5 w-3.5" />
              Revoke access
            </button>
          </div>
        </div>
      )}

      {status === 'revoked' && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2 py-0.5 rounded bg-red-50 text-red-700 text-[11px] font-medium">Revoked</span>
          </div>
          <p className="text-[12px] text-slate-500 mb-3">
            Revoked {record.revoked_at?.slice(0, 10) || '-'} by {record.revoked_by || 'system'}
            {record.revoked_reason ? <> — {record.revoked_reason}</> : null}
          </p>
          <button
            type="button"
            onClick={provision}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-[13px] hover:bg-slate-50 disabled:opacity-50"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Provision new access
          </button>
        </div>
      )}
    </div>
  );
};

export default GateAccessSection;
